import { useEffect, useState } from 'react';

import { getWorkflow } from '$api/WorkflowsAPI';

import type { SubWorkflowMeta } from './editor.types';

/** Loads and caches the interface declared by a referenced sub-workflow. */
export function useSubWorkflowMeta(
  workflowId: string | undefined,
  token: string | null,
) {
  const [subMeta, setSubMeta] = useState<Record<string, SubWorkflowMeta>>({});

  useEffect(() => {
    if (!workflowId || workflowId in subMeta || !token) {
      return;
    }

    let cancelled = false;
    getWorkflow(token, workflowId)
      .then((data) => {
        if (!data.head) {
          throw new Error(`Workflow "${workflowId}" was not found.`);
        }
        if (!cancelled) {
          setSubMeta((current) => ({
            ...current,
            [workflowId]: {
              versions: data.versions
                .filter(
                  (version) =>
                    version.status === 'active' ||
                    version.status === 'archived',
                )
                .toSorted((left, right) => {
                  if (left.version === data.head.activeVersion) {
                    return -1;
                  }
                  if (right.version === data.head.activeVersion) {
                    return 1;
                  }
                  return right.version - left.version;
                }),
              exists: true,
              trigger: data.head.trigger,
              activeVersion: data.head.activeVersion,
            },
          }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSubMeta((current) => ({
            ...current,
            [workflowId]: {
              versions: [],
              exists: false,
              activeVersion: null,
            },
          }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [subMeta, token, workflowId]);

  return subMeta;
}
